/**
 * Projects Sidebar
 * Workspace dropdown + flat board list (local-first, synced to Convex).
 * This is the single place to manage boards — replaces the old BoardsMenu.
 */

import {
  boardErrorAtom,
  boardsAPIAtom,
  boardsAtom,
  createBoardAtom,
  currentBoardIdAtom,
  currentWorkspaceIdAtom,
  deleteBoardAtom,
  editingBoardIdAtom,
  isLoadingBoardsAtom,
  refreshBoardsAtom,
  switchBoardAtom,
  updateBoardNameAtom,
} from "@/core/atoms/boards";
import { useAtom, useAtomValue, useSetAtom } from "@/core/editor-jotai";
import type { Workspace } from "@/core/storage/types";
import { hybridStorageAdapter } from "@/data/HybridStorageAdapter";
import { useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import "./ProjectsSidebar.scss";

export const ProjectsSidebar: React.FC = () => {
  const { user, isLoaded } = useUser();

  // Local-first board state (jotai)
  const boards = useAtomValue(boardsAtom);
  const currentBoardId = useAtomValue(currentBoardIdAtom);
  const isLoading = useAtomValue(isLoadingBoardsAtom);
  const boardsAPI = useAtomValue(boardsAPIAtom);
  const [editingBoardId, setEditingBoardId] = useAtom(editingBoardIdAtom);
  const [newBoardName, setNewBoardName] = useState("");

  // Workspace state
  const [currentWorkspaceId, setCurrentWorkspaceId] = useAtom(currentWorkspaceIdAtom);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  const refreshBoards = useSetAtom(refreshBoardsAtom);
  const createBoard = useSetAtom(createBoardAtom);
  const switchBoard = useSetAtom(switchBoardAtom);
  const updateBoardName = useSetAtom(updateBoardNameAtom);
  const deleteBoard = useSetAtom(deleteBoardAtom);
  const boardError = useAtomValue(boardErrorAtom);

  // Load workspaces on mount
  useEffect(() => {
    if (!user) {
      return;
    }
    setWorkspacesLoading(true);
    hybridStorageAdapter
      .getWorkspaces()
      .then((ws) => {
        setWorkspaces(ws);
        // If no workspace selected yet, pick from localStorage or first
        if (!currentWorkspaceId) {
          const saved = localStorage.getItem("selectedWorkspaceId");
          if (saved && ws.some((w) => w.id === saved)) {
            setCurrentWorkspaceId(saved);
          } else if (ws.length > 0) {
            setCurrentWorkspaceId(ws[0].id);
            localStorage.setItem("selectedWorkspaceId", ws[0].id);
          }
        }
      })
      .catch(console.error)
      .finally(() => setWorkspacesLoading(false));
  }, [user, currentWorkspaceId, setCurrentWorkspaceId]);

  // Set workspace on cloud adapter and refresh boards when workspace changes
  useEffect(() => {
    if (!boardsAPI || !currentWorkspaceId) {
      return;
    }
    // setWorkspaceId is called inside setWorkspaceAndBoard, but we also
    // need to handle the case when only workspace changes (not board)
    hybridStorageAdapter
      .setWorkspaceAndBoard(
        currentWorkspaceId,
        localStorage.getItem("drawink-current-board-id") || "",
      )
      .then(() => refreshBoards())
      .catch(console.error);
  }, [boardsAPI, currentWorkspaceId, refreshBoards]);

  const handleWorkspaceChange = (wsId: string) => {
    setCurrentWorkspaceId(wsId);
    localStorage.setItem("selectedWorkspaceId", wsId);
  };

  const handleCreateBoard = async () => {
    try {
      const name = `Untitled Board ${boards.length + 1}`;
      await createBoard(name);
    } catch (error) {
      console.error("Failed to create board:", error);
    }
  };

  const handleSwitchBoard = async (id: string) => {
    if (id === currentBoardId) return;
    try {
      await switchBoard(id);
      await refreshBoards();
    } catch (error) {
      console.error("Failed to switch board:", error);
    }
  };

  const handleDeleteBoard = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm("Delete this board? This cannot be undone.")) {
      try {
        await deleteBoard(id);
      } catch (error) {
        console.error("Failed to delete board:", error);
      }
    }
  };

  const startEditing = (e: React.MouseEvent, board: { id: string; name: string }) => {
    e.stopPropagation();
    setEditingBoardId(board.id);
    setNewBoardName(board.name);
  };

  const saveBoardName = async (e: React.MouseEvent | React.FormEvent, id: string) => {
    e.stopPropagation();
    if (newBoardName.trim()) {
      await updateBoardName({ id, name: newBoardName.trim() });
    } else {
      setEditingBoardId(null);
    }
  };

  if (!isLoaded || !user) {
    return (
      <div className="projects-sidebar">
        <div className="projects-sidebar__empty">
          <p>Sign in to see your boards</p>
        </div>
      </div>
    );
  }

  if (!boardsAPI) {
    return (
      <div className="projects-sidebar">
        <div className="projects-sidebar__loading">
          <div className="spinner-small" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const currentWorkspace = workspaces.find((ws) => ws.id === currentWorkspaceId);

  return (
    <div className="projects-sidebar">
      {/* Workspace selector */}
      {workspaces.length > 0 && (
        <div className="projects-sidebar__workspace-selector">
          <select
            value={currentWorkspaceId || ""}
            onChange={(e) => handleWorkspaceChange(e.target.value)}
            className="projects-sidebar__workspace-select"
            disabled={workspacesLoading}
          >
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Header with create button */}
      <div className="projects-sidebar__header">
        <h3>{currentWorkspace ? `${currentWorkspace.name}` : "Boards"}</h3>
        <button
          type="button"
          onClick={handleCreateBoard}
          className="projects-sidebar__create-btn"
          title="Create new board"
        >
          +
        </button>
      </div>

      {/* Board list */}
      {isLoading && boards.length === 0 ? (
        <div className="projects-sidebar__loading">
          <div className="spinner-small" />
          <span>Loading boards...</span>
        </div>
      ) : boards.length === 0 ? (
        <div className="projects-sidebar__empty">
          <svg
            aria-hidden="true"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No boards yet</p>
          <button type="button" onClick={handleCreateBoard} className="btn-small">
            Create Board
          </button>
        </div>
      ) : (
        <div className="projects-sidebar__list" role="listbox" aria-label="Boards" tabIndex={0}>
          {boardError && (
            <div className="projects-sidebar__error" role="alert">
              {boardError}
            </div>
          )}
          {boards.map((board) => (
            <div
              key={board.id}
              className={`board-item ${board.id === currentBoardId ? "board-item--active" : ""}`}
              onClick={() => handleSwitchBoard(board.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSwitchBoard(board.id);
                }
              }}
              role="option"
              aria-selected={board.id === currentBoardId}
              tabIndex={0}
            >
              {editingBoardId === board.id ? (
                <div className="board-item__edit" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveBoardName(e, board.id);
                      if (e.key === "Escape") setEditingBoardId(null);
                    }}
                    onBlur={() => setEditingBoardId(null)}
                  />
                  <button
                    type="button"
                    className="board-item__save-btn"
                    onMouseDown={(e) => saveBoardName(e, board.id)}
                  >
                    <svg
                      aria-hidden="true"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <svg
                    aria-hidden="true"
                    className="board-item__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="board-item__name">{board.name}</span>
                  <div className="board-item__actions">
                    <button
                      type="button"
                      className="board-item__action-btn"
                      onClick={(e) => startEditing(e, board)}
                      title="Rename"
                    >
                      <svg
                        aria-hidden="true"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    {boards.length > 1 && (
                      <button
                        type="button"
                        className="board-item__action-btn board-item__action-btn--delete"
                        onClick={(e) => handleDeleteBoard(e, board.id)}
                        title="Delete"
                      >
                        <svg
                          aria-hidden="true"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
